import React, { memo, useMemo } from 'react';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { DiaryEntrySyncStatus } from 'graphql/types';
import {
  getFullHoursMinutesMaskDuration,
  useFormattedDuration
} from 'modules/notes/common';
import { formatTimeFromIsoString } from 'utils/date-time';
import { useMeContext } from 'modules/me';
import { useFeatureFlag } from 'modules/common/hooks';
import { SmartDiaryEntry } from '../type';

type SmartListItemDurationProps = {
  smartEntry: SmartDiaryEntry;
};
type StylesProps = {
  strikeThrough: boolean;
  isSynced: boolean;
  isTiwVpatEnabled: boolean;
};

const useStyles = makeStyles((theme) => ({
  duration: {
    width: theme.spacing(8),
    alignSelf: 'center',
    whiteSpace: 'nowrap',
    marginRight: ({ isTiwVpatEnabled }: StylesProps) =>
      isTiwVpatEnabled ? theme.spacing(5.25) : theme.spacing(-0.5),
    color: theme.palette.text.primary,
    opacity: 0.85,
    textDecoration: ({ strikeThrough }: StylesProps) =>
      strikeThrough ? 'line-through' : 'unset',
    fontStyle: ({ isSynced }: StylesProps) => (isSynced ? 'italic' : 'normal')
  },
  time: {
    margin: theme.spacing(1, -0.25, 1, -1),
    textAlign: 'end'
  },
  durationNewForamt: {
    width: theme.spacing(8),
    textAlign: 'end'
  }
}));

export const SmartListItemDuration: React.FC<SmartListItemDurationProps> = memo(
  ({ smartEntry }: SmartListItemDurationProps) => {
    const me = useMeContext();
    const formattedDuration = useFormattedDuration({
      needsMask: true,
      duration: smartEntry?.duration?.value ?? 0
    });

    const hasStartEndTime = useMemo(
      () =>
        Boolean(smartEntry?.startTime && smartEntry?.endTime) ||
        smartEntry?.duration?.value === null,
      [smartEntry?.duration?.value, smartEntry?.endTime, smartEntry?.startTime]
    );

    const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');
    const fullhoursMinuteDuration = getFullHoursMinutesMaskDuration(
      smartEntry?.duration?.value ?? 0
    );

    const classes = useStyles({
      strikeThrough: !(smartEntry.timeSignals ?? []).length,
      isSynced: smartEntry.syncStatus === DiaryEntrySyncStatus.Synced,
      isTiwVpatEnabled
    });
    return (
      <>
        {hasStartEndTime ? (
          <div className={classes.time}>
            <Typography
              data-testid={`smart-entry-item_time_${smartEntry.id}`}
              variant={'caption'}
              className={classNames(classes.duration, {
                [classes.time]: Boolean(
                  smartEntry?.startTime && smartEntry?.startTime
                )
              })}
            >
              {formatTimeFromIsoString(smartEntry?.startTime ?? '', me)}
              {smartEntry?.startTime && <>&nbsp; &ndash; &nbsp;</>}
              {formatTimeFromIsoString(smartEntry?.endTime ?? '', me)}
            </Typography>
          </div>
        ) : (
          <Typography
            data-testid={`smart-entry-item_duration_${smartEntry.id}`}
            variant={'body2'}
            className={classNames(classes.duration, classes.durationNewForamt)}
            role="button"
            aria-label={fullhoursMinuteDuration}
            aria-roledescription="duration"
          >
            {formattedDuration}
          </Typography>
        )}
      </>
    );
  }
);

SmartListItemDuration.displayName = 'SmartListItemDuration';
